import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { X, Send, Loader2, Mail, AlertTriangle, MessageSquare } from 'lucide-react';
import { BACKEND_URL } from '../lib/backendUrl';

const API = BACKEND_URL + '/api';

export const ContactChat = ({ contact, token, onClose, initialChannel = 'email' }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [channel, setChannel] = useState(
    initialChannel === 'sms' && contact.phone ? 'sms' : (contact.email ? 'email' : 'sms')
  );
  const bottomRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchMessages = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/crm/contacts/${contact.id}/messages`, { headers });
      setMessages(res.data.messages || []);
    } catch { /* */ }
    setLoading(false);
  }, [contact.id, token]);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/crm/contacts/${contact.id}/messages`, { body: text, channel }, { headers });
      setMessages(m => [...m, res.data]);
      setInput('');
    } catch { /* */ }
    setSending(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose} data-testid="contact-chat-overlay">
      <div
        className="w-full max-w-md bg-[#0d1f35] border border-white/10 rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        style={{ height: 'min(560px, 85vh)' }}
        onClick={e => e.stopPropagation()}
        data-testid="contact-chat-panel"
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10 bg-gradient-to-r from-purple-600/20 to-blue-600/10">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center flex-shrink-0">
            <span className="text-white font-semibold text-sm">{(contact.name || '?')[0].toUpperCase()}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white font-medium text-sm truncate">{contact.name || 'Contact'}</p>
            <p className="text-gray-400 text-xs truncate flex items-center gap-1">
              {channel === 'sms' ? <><MessageSquare className="w-3 h-3" /> {contact.phone}</> : <><Mail className="w-3 h-3" /> {contact.email}</>}
            </p>
          </div>
          <div className="flex rounded-lg overflow-hidden border border-white/10 mr-1">
            <button
              onClick={() => contact.email && setChannel('email')}
              disabled={!contact.email}
              title={contact.email ? 'Send by email' : 'No email address'}
              className={`px-2.5 py-1 text-[11px] font-medium transition-colors ${channel === 'email' ? 'bg-purple-600 text-white' : 'bg-transparent text-gray-400 hover:text-white'} ${!contact.email ? 'opacity-40 cursor-not-allowed' : ''}`}
              data-testid="contact-chat-channel-email"
            >
              Email
            </button>
            <button
              onClick={() => contact.phone && setChannel('sms')}
              disabled={!contact.phone}
              title={contact.phone ? 'Send by SMS' : 'No phone number'}
              className={`px-2.5 py-1 text-[11px] font-medium transition-colors ${channel === 'sms' ? 'bg-cyan-600 text-white' : 'bg-transparent text-gray-400 hover:text-white'} ${!contact.phone ? 'opacity-40 cursor-not-allowed' : ''}`}
              data-testid="contact-chat-channel-sms"
            >
              SMS
            </button>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="contact-chat-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Messages (filtered by selected channel) */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {(() => {
            const visible = messages.filter(m => (m.channel || 'email') === channel);
            return loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 text-gray-500 animate-spin" /></div>
          ) : visible.length === 0 ? (
            <div className="text-center py-10">
              {channel === 'sms' ? <MessageSquare className="w-8 h-8 text-gray-600 mx-auto mb-2" /> : <Mail className="w-8 h-8 text-gray-600 mx-auto mb-2" />}
              <p className="text-gray-500 text-sm">No {channel === 'sms' ? 'SMS' : 'email'} messages yet.</p>
              <p className="text-gray-600 text-xs mt-1">Messages you send here go to {contact.name || 'the contact'} by {channel === 'sms' ? 'SMS' : 'email'}. Their replies appear right here in this thread.</p>
            </div>
          ) : (
            visible.map(m => {
              const isInbound = m.direction === 'inbound';
              return (
                <div key={m.id} className={`flex ${isInbound ? 'justify-start' : 'justify-end'}`} data-testid={`contact-chat-message-${m.id}`}>
                  <div className="max-w-[80%]">
                    <div className={`text-sm rounded-2xl px-3.5 py-2 whitespace-pre-line ${isInbound ? 'bg-gray-200 text-gray-900 dark:bg-white/10 dark:text-gray-100 rounded-bl-sm' : 'bg-purple-600 text-white rounded-br-sm'}`}>
                      {m.body}
                    </div>
                    <div className={`flex items-center gap-1.5 mt-1 ${isInbound ? 'justify-start' : 'justify-end'}`}>
                      {m.channel === 'sms' && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">SMS</span>
                      )}
                      {m.status === 'failed' && (
                        <span className="flex items-center gap-1 text-[10px] text-red-400" title={m.error || ''}><AlertTriangle className="w-3 h-3" /> {m.error ? m.error : 'failed to send'}</span>
                      )}
                      {m.status !== 'failed' && m.delivery_status === 'delivered' && (
                        <span className="text-[10px] text-emerald-400">delivered</span>
                      )}
                      <span className="text-[10px] text-gray-500">{m.created_at ? new Date(m.created_at).toLocaleString() : ''}</span>
                    </div>
                  </div>
                </div>
              );
            })
          );
          })()}
          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <div className="border-t border-white/10 p-3">
          <div className="flex items-end gap-2">
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
              placeholder={`Message ${contact.name || 'contact'}... (sent by ${channel === 'sms' ? 'SMS' : 'email'})`}
              rows={2}
              className="flex-1 bg-black/30 border border-white/10 rounded-xl text-white text-sm px-3 py-2 resize-none focus:border-purple-500/50 outline-none placeholder:text-gray-600"
              data-testid="contact-chat-input"
            />
            <Button
              onClick={sendMessage}
              disabled={sending || !input.trim()}
              className="bg-purple-600 hover:bg-purple-700 text-white h-10 w-10 p-0 rounded-xl flex-shrink-0"
              data-testid="contact-chat-send"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
          <p className="text-[10px] text-gray-600 mt-1.5">Delivered by {channel === 'sms' ? 'SMS' : 'email'} · replies appear in this thread</p>
        </div>
      </div>
    </div>
  );
};

export default ContactChat;
